import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import ProductCard, { ProductCardProduct } from '../../components/common/ProductCard';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import '../../components/common/ProductCard.css';
import './LocalShop.css';

interface Product {
  id: number;
  name: string;
  brand: string;
  category: string;
  product_line: string;
  size_lbs: string;
  price: string;
  is_byoc: boolean;
  image_url?: string;
  form?: string;
}

interface Cart {
  id: number;
  items: Array<{ id: number; product: Product; quantity: number; line_total: string }>;
  item_count: number;
  subtotal: string;
}

interface Category {
  value: string;
  label: string;
}

type SortOption = 'name-asc' | 'price-low' | 'price-high';

const CATEGORY_ORDER = ['Livestock Feed', 'Dog', 'Cat', 'Wild Bird', 'Other'];

const LocalShop: React.FC = () => {
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [categories, setCategories] = useState<Category[]>([{ value: '', label: 'All Products' }]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState<number | null>(null);

  const loadCategories = async () => {
    try {
      const data = await bridgeClubAPI.getLocalCategories();
      if (data && data.length > 0) {
        setCategories(data);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    loadCategories();
    loadProducts();
    loadCart();
  }, [hasLocalAccess, navigate]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getLocalProducts(
        selectedCategory || undefined,
        searchQuery || undefined
      );
      setProducts(data || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCart = async () => {
    try {
      const data = await bridgeClubAPI.getLocalCart();
      setCart(data);
    } catch (err) {
      console.error('Failed to load cart:', err);
    }
  };

  useEffect(() => {
    if (hasLocalAccess) {
      loadProducts();
    }
  }, [selectedCategory, searchQuery, hasLocalAccess]);

  const handleAddToCart = async (product: Product) => {
    try {
      setAddingToCart(product.id);
      const updatedCart = await bridgeClubAPI.addToLocalCart(product.id, 1);
      setCart(updatedCart);
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setAddingToCart(null);
    }
  };

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case 'price-high':
        return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      default:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [products, sortBy]);

  // Group products by category when "All" is selected
  const groupedProducts = useMemo(() => {
    if (selectedCategory || searchQuery) {
      return null; // No grouping when filtered
    }

    const groups: Record<string, Product[]> = {};
    for (const product of sortedProducts) {
      const cat = product.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(product);
    }

    // Sort groups by CATEGORY_ORDER
    const orderedGroups: Array<{ category: string; products: Product[] }> = [];
    for (const cat of CATEGORY_ORDER) {
      if (groups[cat]) {
        orderedGroups.push({ category: cat, products: groups[cat] });
        delete groups[cat];
      }
    }
    // Any remaining categories not in the order
    for (const [cat, prods] of Object.entries(groups)) {
      orderedGroups.push({ category: cat, products: prods });
    }

    return orderedGroups;
  }, [sortedProducts, selectedCategory, searchQuery]);

  const toCardProduct = (p: Product): ProductCardProduct => ({
    id: p.id,
    name: p.name,
    brand: p.brand,
    form: p.form,
    category: p.category,
    sizeLbs: p.size_lbs,
    price: p.price,
    isByoc: p.is_byoc,
  });

  if (!hasLocalAccess) {
    return null;
  }

  return (
    <BridgeClubLayout>
      <div className="local-shop">
        <div className="shop-header">
          <h1>Mill Store</h1>
          <div className="header-actions">
            <button
              className="bulk-order-link"
              onClick={() => navigate('/bridge-club/bulk-order')}
            >
              Bulk Pre-Order
            </button>
            {cart && cart.item_count > 0 && (
              <button
                className="cart-button"
                onClick={() => navigate('/bridge-club/cart')}
              >
                Cart ({cart.item_count})
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-pills">
            {categories.map((cat) => (
              <button
                key={cat.value}
                className={`filter-pill ${selectedCategory === cat.value ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="filter-search-wrap">
            <span className="search-icon">&#128269;</span>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="filter-search"
            />
          </div>
          <select
            className="filter-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
          >
            <option value="name-asc">A-Z</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>

        {loading ? (
          <div className="shop-loading">
            <div className="spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="shop-empty">
            <p>No products found</p>
          </div>
        ) : groupedProducts ? (
          /* Category-grouped view when "All" is selected */
          groupedProducts.map((group) => (
            <div key={group.category} className="product-grid-section">
              <div className="product-grid-section-header">
                <h2>{group.category}</h2>
                <span className="section-line" />
                <span className="product-grid-section-count">
                  {group.products.length} item{group.products.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="pgrid">
                {group.products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={toCardProduct(product)}
                    onAddToCart={() => handleAddToCart(product)}
                    adding={addingToCart === product.id}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          /* Flat grid when a category or search is active */
          <div className="pgrid">
            {sortedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={toCardProduct(product)}
                onAddToCart={() => handleAddToCart(product)}
                adding={addingToCart === product.id}
              />
            ))}
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default LocalShop;
